import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  List,
  ListItem,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FilterFramesIcon from "@mui/icons-material/FilterFrames";
import { QRCodeSVG } from "qrcode.react";
import getOrders from "../api/app/getOrders";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { AuthContext } from "../context/AuthContext";
import refundOrder from "../api/order/refundOrder";
import ConfirmDialog from "../component/ConfirmDialog";
import ExtendDialog from "../component/ExtendDialog";

const MyOrderPage = () => {
  const [orders, setOrders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState("");
  const [dialogTitle, setDialogTitle] = useState("");
  const [confirmOnly, setConfirmOnly] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showQrCodeId, setShowQrCodeId] = useState(null); // Track which order's QR code is shown

  const navigate = useNavigate();
  const theme = useTheme();
  const userContext = useContext(AuthContext);

  const handleRefund = async (orderId) => {
    try {
      const response = await refundOrder(orderId);
      if (response.code === "000") {
        setOrders((prev) => prev.filter((o) => o.order.id !== orderId));
        setDialogOpen(false);
      } else {
        throw new Error("Refund process failed.");
      }
    } catch (error) {
      setDialogTitle("退款失敗");
      setDialogContent("退款未成功。可能退款次數達到上限或稍後重試。");
      setConfirmOnly(true);
      setDialogOpen(true);
    }
  };

  const onRefundClicked = (order) => {
    setCurrentOrder(order);
    setDialogTitle("取消訂單");
    setDialogContent("退費將會以優惠券形式發放，每個月可以使用三次，請確認是否取消訂單？");
    setConfirmOnly(false);
    setDialogOpen(true);
  };

  const onExtendClicked = (order) => {
    setCurrentOrder(order);
    setExtendDialogOpen(true);
  };

  const handleExtendDialogClose = () => setExtendDialogOpen(false);
  const handleDialogClose = () => setDialogOpen(false);

  useEffect(() => {
    async function fetchData() {
      console.log("[MyOrderPage] Fetching orders, token:", userContext.token ? "exists" : "null");
      try {
        const response = await getOrders();
        console.log("[MyOrderPage] getOrders response:", response);
        if (response.data) {
          setOrders(response.data);
        }
      } catch (error) {
        console.error("[MyOrderPage] Failed to fetch orders", error);
      }
    }
    if (userContext.token) fetchData();
  }, [userContext.token]);

  const main = theme.palette.info.main;

  return (
    <Box
      width="100%"
      height="100vh"
      sx={{ backgroundColor: theme.palette.background.default }}
    >
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: theme.palette.appbar || theme.palette.background.default,
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            aria-label="back"
            onClick={() => navigate(-1)}
            sx={{ color: theme.palette.text.primary }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{ pl: 2, fontSize: "h6.fontSize", fontWeight: "bold" }}
            color="text.primary"
          >
            我的訂單
          </Typography>
        </Toolbar>
      </AppBar>

      <Box m={1} />

      {userContext.token === null ? (
        <Typography
          mt={20}
          fontSize={"h5.fontSize"}
          fontWeight={"bold"}
          align="center"
          color="text.primary"
        >
          尚未登入
        </Typography>
      ) : (
        <List
          component="nav"
          aria-label="user orders"
          sx={{ backgroundColor: theme.palette.background.default }}
        >
          {orders?.map((orderData, index) => {
            // 支援 nested (order.order) 或 flat 結構
            const orderId = orderData.order?.id ?? orderData.id;
            const simulatorName = orderData.storeSimulator?.name ?? orderData.name ?? "未知";
            const storeName = orderData.storeSimulator?.store?.name ?? orderData.storeName ?? "";
            const startTime = orderData.startTime;
            const endTime = orderData.endTime;
            const amount = orderData.amount;

            return (
              <React.Fragment key={orderId}>
                <ListItem disableGutters sx={{ px: 2 }}>
                  <Card
                    variant="outlined"
                    sx={{
                      width: "100%",
                      bgcolor: theme.palette.background.default,
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                      borderRadius: 2,
                    }}
                  >
                    <CardHeader
                      sx={{
                        bgcolor: theme.palette.background.default,
                        "& .MuiCardHeader-title": { color: theme.palette.text.primary, fontWeight: 700 },
                        "& .MuiCardHeader-subheader": { color: theme.palette.text.secondary },
                      }}
                      avatar={<FilterFramesIcon sx={{ color: main }} />}
                      title={simulatorName}
                      subheader={storeName}
                    />
                    <Divider />
                    <CardContent sx={{ bgcolor: theme.palette.background.default }}>
                      <Typography variant="body2" color="text.secondary">
                        {`編號：${orderId}`}
                      </Typography>

                      <Typography variant="body2" color="text.primary" sx={{ mt: 0.5 }}>
                        {`訂位時間：${formatInTimeZone(parseISO(startTime), "UTC", "yyyy/MM/dd HH:mm")} ~ ${formatInTimeZone(
                          parseISO(endTime),
                          "UTC",
                          "yyyy/MM/dd HH:mm"
                        )}`}
                      </Typography>

                      <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, fontWeight: 700 }}>
                        {`總計：$${amount}`}
                      </Typography>

                      {/* 入場碼 */}
                      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
                        {showQrCodeId === orderId ? (
                          <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                              入場碼
                            </Typography>
                            <Box
                              sx={{
                                bgcolor: "#fff",
                                borderRadius: 2,
                                boxShadow: `0 0 12px ${main}66`,
                                p: 1,
                                mb: 1,
                              }}
                            >
                              <QRCodeSVG value={orderId} size={120} />
                            </Box>
                            <Button
                              variant="text"
                              size="small"
                              color="info"
                              onClick={() => setShowQrCodeId(null)}
                            >
                              收起
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            color="info"
                            onClick={() => setShowQrCodeId(orderId)}
                            sx={{ fontWeight: 600 }}
                          >
                            顯示入場碼
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        color="info"
                        onClick={() =>
                          onExtendClicked({
                            OrderId: orderId,
                            StoreCode: orderData.storeSimulator?.store?.id || orderData.storeId || "",
                            TableCode: orderData.storeSimulator?.code || orderData.tableCode || "",
                            ReservationEndTime: endTime,
                            VenueId: orderData.storeSimulator?.id || orderData.storeSimulatorId || "",
                            storeSimulatorId: orderData.storeSimulator?.id || orderData.storeSimulatorId || "",
                          })
                        }
                      >
                        延長
                      </Button>
                    </CardActions>
                  </Card>
                </ListItem>
                {index !== orders.length - 1 && (
                  <Divider variant="middle" sx={{ borderColor: theme.palette.divider }} />
                )}
              </React.Fragment>
            );
          })}
        </List>
      )}

      <ConfirmDialog
        open={dialogOpen}
        onConfirm={confirmOnly ? handleDialogClose : () => handleRefund(currentOrder.order.id)}
        onClose={handleDialogClose}
        title={dialogTitle}
        content={dialogContent}
        confirmOnly={confirmOnly}
      />
      <ExtendDialog open={extendDialogOpen} onClose={handleExtendDialogClose} order={currentOrder} />
    </Box>
  );
};

export default MyOrderPage;
